import type { TestContext } from "../types";

/**
 * Information about a group of test steps
 */
export interface GroupInfo {
  groupName: string;
  startIndex: number;
  endIndex: number;
  isCollapsed?: boolean;
  stepIds: string[];
}

/**
 * Metadata about a step's relationship to groups
 */
export interface StepGroupMetadata {
  isGroupCheck: boolean;
  groupName: string | null;
  indentLevel: number;
  belongsToGroup: string | null;
}

/**
 * Analyze all contexts and extract group information
 *
 * @param contexts - Array of test contexts in order
 * @returns Map of group name to group info
 */
export function analyzeGroups(contexts: TestContext[]): Map<string, GroupInfo> {
  const groups = new Map<string, GroupInfo>();

  for (let i = 0; i < contexts.length; i++) {
    const ctx = contexts[i];
    const groupName = ctx.data?.group;

    if (!groupName) {
      continue;
    }

    // Check if this is the first occurrence of this group (group check)
    const existingGroup = groups.get(groupName);

    if (!existingGroup) {
      // This is a new group - this step is the group check
      groups.set(groupName, {
        groupName,
        startIndex: i,
        endIndex: i,
        stepIds: [ctx.id],
      });
    } else {
      // This step belongs to an existing group
      // Check if it's contiguous with the group
      if (i === existingGroup.endIndex + 1) {
        // Extend the group
        existingGroup.endIndex = i;
        existingGroup.stepIds.push(ctx.id);
      } else {
        // Non-contiguous group - treat as a new group
        // This shouldn't normally happen, but handle it gracefully
        const newGroupName = `${groupName}_${i}`;
        groups.set(newGroupName, {
          groupName: newGroupName,
          startIndex: i,
          endIndex: i,
          stepIds: [ctx.id],
        });
      }
    }
  }

  return groups;
}

/**
 * Get metadata about a step's relationship to groups
 *
 * @param ctx - The test context to analyze
 * @param allContexts - Array of all test contexts in order
 * @returns Metadata about the step's group relationship
 */
export function getStepGroupMetadata(
  ctx: TestContext,
  allContexts: TestContext[],
): StepGroupMetadata {
  const groupName = ctx.data?.group || null;

  if (!groupName) {
    return {
      isGroupCheck: false,
      groupName: null,
      indentLevel: 0,
      belongsToGroup: null,
    };
  }

  // Find the index of this context
  const currentIndex = allContexts.findIndex((c) => c.id === ctx.id);

  if (currentIndex === -1) {
    return {
      isGroupCheck: false,
      groupName,
      indentLevel: 0,
      belongsToGroup: groupName,
    };
  }

  // Check if this is the first occurrence of this group name
  let isGroupCheck = true;
  for (let i = 0; i < currentIndex; i++) {
    if (allContexts[i].data?.group === groupName) {
      isGroupCheck = false;
      break;
    }
  }

  return {
    isGroupCheck,
    groupName,
    indentLevel: isGroupCheck ? 0 : 1,
    belongsToGroup: groupName,
  };
}

/**
 * Get all contexts that belong to a specific group
 *
 * @param groupName - The group name to search for
 * @param contexts - Array of all test contexts
 * @returns Array of contexts in the group
 */
export function getGroupMembers(groupName: string, contexts: TestContext[]): TestContext[] {
  const groups = analyzeGroups(contexts);
  const groupInfo = groups.get(groupName);

  if (!groupInfo) {
    return [];
  }

  return contexts.filter((ctx) => groupInfo.stepIds.includes(ctx.id));
}

/**
 * Check if moving a step from one position to another is valid
 * (doesn't break group structure)
 *
 * @param fromIndex - Source index
 * @param toIndex - Target index
 * @param contexts - Array of all test contexts
 * @returns True if the move is valid
 */
export function isValidGroupMove(
  fromIndex: number,
  toIndex: number,
  contexts: TestContext[],
): boolean {
  if (fromIndex === toIndex) {
    return true;
  }

  const sourceCtx = contexts[fromIndex];
  const sourceMetadata = getStepGroupMetadata(sourceCtx, contexts);

  // If moving a group check, we need special handling
  // (should move entire group, not just the check)
  if (sourceMetadata.isGroupCheck) {
    // This will be handled separately with confirmation
    // For now, allow it
    return true;
  }

  // If moving a grouped step, check if it's staying within valid boundaries
  if (sourceMetadata.belongsToGroup) {
    const groups = analyzeGroups(contexts);
    const groupInfo = groups.get(sourceMetadata.belongsToGroup);

    if (!groupInfo) {
      return true;
    }

    // Can't move a grouped step outside its group boundaries
    // unless moving it to remove from group (to a position with no group)
    const targetCtx = contexts[toIndex];
    const targetMetadata = getStepGroupMetadata(targetCtx, contexts);

    // If target is in a different group or no group, it's a removal from group
    if (!targetMetadata.groupName || targetMetadata.groupName !== sourceMetadata.belongsToGroup) {
      // Allow removal from group
      return true;
    }

    // Moving within the same group is always OK
    if (targetMetadata.belongsToGroup === sourceMetadata.belongsToGroup) {
      return true;
    }
  }

  // Non-grouped steps can move anywhere
  return true;
}

/**
 * Get array of unique group names from contexts
 *
 * @param contexts - Array of test contexts
 * @returns Array of unique group names
 */
export function getExistingGroupNames(contexts: TestContext[]): string[] {
  const groupNames = new Set<string>();

  for (const ctx of contexts) {
    if (ctx.data?.group) {
      groupNames.add(ctx.data.group);
    }
  }

  return Array.from(groupNames).sort();
}

/**
 * Check if a context is currently visible (not hidden by collapsed group)
 *
 * @param ctx - The test context to check
 * @param allContexts - Array of all test contexts
 * @param collapsedGroups - Set of collapsed group names
 * @returns True if the context should be visible
 */
export function isContextVisible(
  ctx: TestContext,
  allContexts: TestContext[],
  collapsedGroups: Set<string>,
): boolean {
  const metadata = getStepGroupMetadata(ctx, allContexts);

  // Group checks are always visible
  if (metadata.isGroupCheck) {
    return true;
  }

  // If belongs to a collapsed group, hide it
  if (metadata.belongsToGroup && collapsedGroups.has(metadata.belongsToGroup)) {
    return false;
  }

  // Everything else is visible
  return true;
}

/**
 * Get the visual index of a context (accounting for collapsed groups)
 *
 * @param ctx - The test context to find
 * @param allContexts - Array of all test contexts
 * @param collapsedGroups - Set of collapsed group names
 * @returns Visual index, or -1 if not visible
 */
export function getVisualIndex(
  ctx: TestContext,
  allContexts: TestContext[],
  collapsedGroups: Set<string>,
): number {
  let visualIndex = 0;

  for (const c of allContexts) {
    if (c.id === ctx.id) {
      return isContextVisible(c, allContexts, collapsedGroups) ? visualIndex : -1;
    }

    if (isContextVisible(c, allContexts, collapsedGroups)) {
      visualIndex++;
    }
  }

  return -1;
}
